/**
 * Shared card data for the Admin Dashboard sidebar and category layouts.
 * Card metadata is sourced from adminCardDictionary; this module adds sidebar-only
 * entries and category navigation helpers for CategoryLayout.
 */
import {
    TagIcon,
    BuildingOfficeIcon,
    TruckIcon,
    TrophyIcon,
    Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { CARD_DESCRIPTIONS, getDefaultCardOrganization } from '../utils/dashboardHelpers';
import { getCardStaticData, type CardStaticData } from './adminCardDictionary';

export { CARD_DESCRIPTIONS };

const SIDEBAR_ONLY_CARDS: Record<string, CardStaticData> = {
    'Company Types': { icon: TagIcon, link: '/admin/types?tab=company-types', color: 'bg-pink-500' },
    'Application Types': { icon: Squares2X2Icon, link: '/admin/types?tab=application-types', color: 'bg-purple-600' },
    'Property Types': { icon: BuildingOfficeIcon, link: '/admin/types?tab=real-estate-property-types', color: 'bg-cyan-600' },
    'Sport Types': { icon: TrophyIcon, link: '/admin/types?tab=sports-sport-types', color: 'bg-cyan-500' },
    'Vehicle Types': { icon: TruckIcon, link: '/admin/types?tab=automotive-vehicle-types', color: 'bg-amber-600' },
    'Automotive Manufacturers': { icon: TagIcon, link: '/admin/types?tab=automotive-manufacturers', color: 'bg-amber-500' },
};

export type { CardStaticData };

export const CARD_STATIC_DATA: Record<string, CardStaticData> = {
    ...getCardStaticData(),
    ...SIDEBAR_ONLY_CARDS,
};

export const DEFAULT_CATEGORY_CARDS: Record<string, string[]> = getDefaultCardOrganization();

export interface CategoryInfo {
    name: string;
    gradient: { dark: string; light: string };
}

export const CATEGORY_INFO: Record<string, CategoryInfo> = {
    'access control': { name: 'Access Control', gradient: { dark: 'from-indigo-400 to-blue-400', light: 'from-indigo-600 to-blue-600' } },
    system: { name: 'System', gradient: { dark: 'from-violet-400 to-purple-400', light: 'from-indigo-600 to-purple-600' } },
    applications: { name: 'Applications', gradient: { dark: 'from-emerald-400 to-green-400', light: 'from-green-600 to-emerald-600' } },
    other: { name: 'Other', gradient: { dark: 'from-slate-400 to-gray-400', light: 'from-gray-600 to-slate-600' } },
};

const HUB_EXCLUSIONS: string[] = [
    '/admin/accounting-management',
    '/admin/accounting',
    '/admin/investor-hub',
    '/admin/system-controls',
    '/admin/ai-management',
    '/admin/daily-quotes',
    '/admin/application-types',
    '/admin/esignature',
    '/admin/geographic-types',
    '/admin/template-types',
    '/admin/tax-types',
    '/admin/template-manager',
    '/admin/support',
    '/admin/support-tickets',
    '/admin/shipping-hub',
    '/admin/hr-management',
    '/admin/user-management-hub',
    '/admin/commodity-marketplace',
    '/admin/crm',
    '/admin/types',
    '/admin/landing-cms',
    '/admin/sports',
    '/admin/automotive',
    '/admin/real-estate',
    '/admin/special-events',
    '/admin/portfolio-hub',
    '/admin/cloud-storage',
];

export function findCategoryForPath(pathname: string): string | null {
    for (const hubPath of HUB_EXCLUSIONS) {
        if (pathname === hubPath || pathname.startsWith(hubPath + '/')) {
            return null;
        }
    }

    for (const [categoryKey, cardNames] of Object.entries(DEFAULT_CATEGORY_CARDS)) {
        for (const cardName of cardNames) {
            const cardData = CARD_STATIC_DATA[cardName];
            if (cardData) {
                const cardPath = cardData.link.split('?')[0];
                if (pathname === cardPath || pathname.startsWith(cardPath + '/')) {
                    return categoryKey;
                }
            }
        }
    }
    return null;
}

export interface CategorySidebarItem {
    name: string;
    icon: CardStaticData['icon'];
    link: string;
    color: string;
    description: string;
}

export function getCategorySidebarItems(categoryKey: string): CategorySidebarItem[] {
    const cardNames = DEFAULT_CATEGORY_CARDS[categoryKey] || [];
    const items: CategorySidebarItem[] = [];
    for (const name of cardNames) {
        const data = CARD_STATIC_DATA[name];
        if (!data) continue;
        items.push({
            name,
            icon: data.icon,
            link: data.link,
            color: data.color,
            description: CARD_DESCRIPTIONS[name] || `Manage ${name.toLowerCase()}.`,
        });
    }
    return items;
}

export function isCategoryLinkActive(itemLink: string, pathname: string, search: string): boolean {
    const [linkPath, linkSearch] = itemLink.split('?');
    if (linkSearch) {
        return pathname === linkPath && search.includes(linkSearch);
    }
    return pathname === linkPath || pathname.startsWith(linkPath + '/');
}
